import React, { useEffect, useRef, useState } from "react";
import * as Babel from "@babel/standalone";
import * as ReactLib from "react";
import * as Mui from "@mui/material";
import * as ReactDOM from "react-dom/client";

export default function DynamicWidgetRenderer({
  code,
  widget,
  onValueUpdate,
  onStateUpdate,
  onTriggerEvent,
  theme,
  Mui,
  React,
  ...otherProps
}) {
  const [error, setError] = useState(null);
  const mountRef = useRef();
  const rootRef = useRef(null);
  const [compiledCode, setCompiledCode] = useState(null);
  const [isReady, setIsReady] = useState(false);

  // Compile code
  useEffect(() => {
    if (!code) {
      setCompiledCode(null);
      setIsReady(false);
      return;
    }

    try {
      const codePatched = code.replace(
        /export\s+default\s+/,
        "exports.default = "
      );
      const compiled = Babel.transform(codePatched, {
        presets: ["env", "react", "typescript"],
        filename: "file.tsx",
      }).code;

      const sandbox = {
        React,
        Mui,
        exports: {},
        module: { exports: {} },
      };

      // eslint-disable-next-line no-new-func
      const fn = new Function(
        "React",
        "Mui",
        "exports",
        "module",
        compiled + "; return module.exports.default || exports.default;"
      );
      const Comp = fn(React, Mui, sandbox.exports, sandbox.module);
      setCompiledCode(() => Comp);
      setError(null);
      setIsReady(true);
    } catch (e) {
      setError(e.message);
      setCompiledCode(null);
      setIsReady(false);
    }
  }, [code, React, Mui]);

  // Handle rendering
  useEffect(() => {
    if (!isReady || !compiledCode || !mountRef.current) return;

    let currentRoot = null;

    const renderComponent = () => {
      if (!mountRef.current) return;

      // Cleanup previous root
      if (rootRef.current) {
        try {
          rootRef.current.unmount();
        } catch (e) {
          console.warn("Error unmounting previous root:", e);
        }
        rootRef.current = null;
      }

      // Create new root and render
      try {
        currentRoot = ReactDOM.createRoot(mountRef.current);
        rootRef.current = currentRoot;
        currentRoot.render(
          React.createElement(compiledCode, {
            widget,
            onValueUpdate,
            onStateUpdate,
            onTriggerEvent,
            theme,
            Mui,
            React,
            ...otherProps,
          })
        );
      } catch (e) {
        setError(e.message);
        if (currentRoot) {
          try {
            currentRoot.unmount();
          } catch (e) {
            console.warn("Error unmounting root after error:", e);
          }
          currentRoot = null;
          rootRef.current = null;
        }
      }
    };

    // Schedule render for next tick
    const timeoutId = setTimeout(renderComponent, 0);

    return () => {
      clearTimeout(timeoutId);
      if (currentRoot) {
        try {
          currentRoot.unmount();
        } catch (e) {
          console.warn("Error unmounting root during cleanup:", e);
        }
        currentRoot = null;
        rootRef.current = null;
      }
    };
  }, [
    isReady,
    compiledCode,
    widget,
    onValueUpdate,
    onStateUpdate,
    onTriggerEvent,
    theme,
    Mui,
    React,
    otherProps,
  ]);

  return (
    <div>
      {error && (
        <div style={{ color: "red" }}>Compile/Runtime Error: {error}</div>
      )}
      <div ref={mountRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}
